import type { Discount, Order, PaymentMethod } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro, type Cents } from "../../lib/money";
import { Dialog } from "../../ui/Dialog";
import { discountLabel } from "../general/general";

export type Settle = { payWith: PaymentMethod } | { refundWith: "cash" | "terminal" };

interface Props {
  /** The order as it was before the edit. */
  order: Order;
  totalCents: Cents;
  changes: { added: string[]; removed: string[] };
  /** "Korting" chosen while editing: the new discount, null when it was taken away; left out when it stays. */
  discount?: Discount | null | undefined;
  /** The methods that may be taken for "Bijbetalen". */
  methods: PaymentMethod[];
  busy: boolean;
  onSettle: (settle: Settle) => void;
  onClose: () => void;
}

/** "Bijbetalen" pays these, in the order of the prototype. */
const PAY_METHODS: PaymentMethod[] = ["cash", "bancontact", "bank_qr", "card"];

/**
 * Saving an edit of a paid order (prototype saveEdit): "Bijbetalen" when the total is higher, "Terug
 * te geven" when it is lower, in cash or through the payment terminal; the owner hears of every
 * refund. The edit and the money are saved in one go.
 */
export function EditSettleDialog({ order, totalCents, changes, discount, methods, busy, onSettle, onClose }: Props) {
  const text = t.editing.settle;
  const difference = totalCents - order.paidCents;
  const up = difference > 0;
  return (
    <Dialog title={up ? text.payTitle : text.refundTitle} size="narrow" onClose={busy ? () => undefined : onClose}>
      <p>
        {text.wasBecomes[0]} <span className="code">{order.code}</span>{" "}
        {text.wasBecomes[1](formatEuro(order.totalCents), formatEuro(totalCents))}
      </p>
      <div className="sum">
        <div className="t">
          <span>{up ? text.due : text.giveBack}</span>
          <span className="due num">{formatEuro(Math.abs(difference))}</span>
        </div>
      </div>
      {changes.added.length > 0 && <p className="proto">{text.added(changes.added.join(", "))}</p>}
      {changes.removed.length > 0 && <p className="proto">{text.removed(changes.removed.join(", "))}</p>}
      {discount !== undefined && (
        <p className="proto">{discount === null ? text.discountOff : text.discount(discountLabel(discount))}</p>
      )}
      <div className="pms">
        {up
          ? PAY_METHODS.filter((method) => methods.includes(method)).map((method) => (
              <button
                key={method}
                type="button"
                className={`pm ${method === "cash" ? "cash-method" : ""}`}
                disabled={busy}
                onClick={() => {
                  onSettle({ payWith: method });
                }}
              >
                {t.paymentMethods[method]}
              </button>
            ))
          : (["cash", "terminal"] as const).map((how) => (
              <button
                key={how}
                type="button"
                className={`pm ${how === "cash" ? "cash-method" : ""}`}
                disabled={busy}
                onClick={() => {
                  onSettle({ refundWith: how });
                }}
              >
                {text.refundWith[how]}
              </button>
            ))}
      </div>
    </Dialog>
  );
}
